'use client'

import type { SortingState } from '@tanstack/react-table'
import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Download, Search } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NumberFlow,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@ui/web/components'
import { typographyVariants, WeekChanger } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import { formatDate, formatSecondsToMinutes } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'
import { SchoolClassSelect } from '../../school-class-select'
import { useColumns } from './columns'
import { countStars, STAR_MIN_MINUTES } from './count-stars'
import { Empty } from './empty'

const coreRowModel = getCoreRowModel()
const filteredRowModel = getFilteredRowModel()
const sortedRowModel = getSortedRowModel()
const getRowId = (row: { id: string }) => row.id
const EMPTY_FILTERS: { id: string; value: string }[] = []

interface ReadingJournalTableProps {
  type: 'web' | 'school'
  data: RouterOutputs['metrics']['getReadingJournal']
  className?: string
  renderProfileLink: RenderLink
  withSearch?: boolean
  withDownloadButton?: boolean
  renderCreateLink?: RenderLink
  /** Monday (start of week) currently shown. The parent owns this state. */
  weekStart: Date
  /** Earliest selectable Monday — navigation cannot go before it. */
  minWeekStart: Date
  /** Called with the new Monday when the user changes weeks. Drives the server refetch. */
  onWeekChange: (monday: Date) => void
  /** School-class filter value (`type === 'school'` only). `null` = all classes. Owned by the parent. */
  schoolClassValue?: string | null
  /** Called when the user picks a class. When provided alongside `type === 'school'`, the class select is shown. */
  onSchoolClassChange?: (value: string | null) => void
  /** Member is not assigned to any class — render a distinct empty state with no create CTA. */
  noClassAssigned?: boolean
}

const DAY_COLUMNS = [
  { label: 'Senin', day: 1 },
  { label: 'Selasa', day: 2 },
  { label: 'Rabu', day: 3 },
  { label: 'Kamis', day: 4 },
  { label: 'Jumat', day: 5 },
  { label: 'Sabtu', day: 6 },
  { label: 'Minggu', day: 0 },
]

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function getSunday(monday: Date): Date {
  const d = new Date(monday)
  d.setDate(d.getDate() + 6)
  d.setHours(23, 59, 59, 999)
  return d
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000

function formatWeekLabel(activeMonday: Date, latestMonday: Date): string {
  const weeksAgo = Math.round((latestMonday.getTime() - activeMonday.getTime()) / MS_PER_WEEK)
  if (weeksAgo <= 0) return 'Minggu ini'
  return `${weeksAgo} minggu lalu`
}

function MetricCard({
  label,
  value,
  unit,
  format,
}: {
  label: string
  value: number
  unit: string
  format?: React.ComponentProps<typeof NumberFlow>['format']
}) {
  return (
    <div className='srd:flex srd:flex-col srd:gap-2 srd:rounded-2xl srd:bg-neutral-white srd:p-4 srd:text-neutral-black'>
      <div className='srd:flex srd:flex-col'>
        <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>{label}</p>
        <NumberFlow
          value={value}
          format={format}
          className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')}
        />
      </div>
      <p className={cn(typographyVariants({ variant: 'body-sm' }))}>{unit}</p>
    </div>
  )
}

export function ReadingJournalTable({
  type,
  data,
  className,
  renderProfileLink,
  withSearch,
  withDownloadButton,
  renderCreateLink,
  weekStart,
  minWeekStart,
  onWeekChange,
  schoolClassValue,
  onSchoolClassChange,
  noClassAssigned,
}: ReadingJournalTableProps) {
  const trpc = useSharedTRPC()
  const posterMutation = useMutation(trpc.metrics.getReadingJournalPoster.mutationOptions())

  const columns = useColumns({ type, renderLink: renderProfileLink })
  const [nameFilter, setNameFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])

  const latestMonday = React.useMemo(() => getMonday(new Date()), [])
  const activeMonday = React.useMemo(() => getMonday(weekStart), [weekStart])
  const earliestMonday = React.useMemo(() => getMonday(minWeekStart), [minWeekStart])

  const canGoLeft = activeMonday.getTime() > earliestMonday.getTime()
  const canGoRight = activeMonday.getTime() < latestMonday.getTime()

  // Week metrics over the currently loaded students (already filtered by week + class on the server).
  const { fiveStarPlusCount, avgStarredDays } = React.useMemo(() => {
    const journals = data.journals
    if (journals.length === 0) return { fiveStarPlusCount: 0, avgStarredDays: 0 }
    let totalStarred = 0
    let fivePlus = 0
    for (const j of journals) {
      const starredDays = countStars(j)
      totalStarred += starredDays
      if (starredDays >= 5) fivePlus += 1
    }
    return { fiveStarPlusCount: fivePlus, avgStarredDays: totalStarred / journals.length }
  }, [data.journals])

  const downloadCSV = () => {
    const rows = table.getFilteredRowModel().rows

    if (rows.length === 0) {
      toast.error('Tidak ada data untuk diunduh.')
      return
    }

    const headers = ['Nama', ...DAY_COLUMNS.map((d) => d.label)]
    const csvRows = rows.map((row) => {
      const dayValues = DAY_COLUMNS.map(({ day }) => {
        const session = row.original.sessions.find((s) => s.dayOfWeek === day)
        return session ? formatSecondsToMinutes(session.totalDurationSeconds) : '-'
      })
      return [row.original.name, ...dayValues].map((v) => `"${v}"`).join(',')
    })

    const csv = [headers.join(','), ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'jurnal-baca.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadPoster = async () => {
    const rows = table.getFilteredRowModel().rows

    if (rows.length === 0) {
      toast.error('Tidak ada data untuk diunduh.')
      return
    }

    try {
      const result = await posterMutation.mutateAsync({
        schoolClassId: schoolClassValue ?? null,
        weekRange: `${formatDate(activeMonday, { dateStyle: 'long' })} - ${formatDate(getSunday(activeMonday), { dateStyle: 'long' })}`,
        students: rows.map((row) => ({
          name: row.original.name,
          image: row.original.image ?? null,
          sessions: row.original.sessions.map((s) => ({
            dayOfWeek: s.dayOfWeek,
            totalDurationSeconds: s.totalDurationSeconds,
          })),
        })),
      })

      const byteCharacters = atob(result.pdf)
      const byteArray = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i)
      }
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'jurnal-baca.pdf'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Gagal membuat poster PDF.')
    }
  }

  const columnFilters = React.useMemo(
    () => (withSearch ? [{ id: 'id', value: nameFilter }] : EMPTY_FILTERS),
    [withSearch, nameFilter],
  )

  const table = useReactTable({
    data: data.journals,
    columns,
    getCoreRowModel: coreRowModel,
    getFilteredRowModel: filteredRowModel,
    getSortedRowModel: sortedRowModel,
    getRowId,
    state: { columnFilters, sorting },
    onSortingChange: setSorting,
  })

  // Member with no assigned class — no classes to filter, so skip the header and show a bare empty state.
  if (noClassAssigned) {
    return (
      <div className={cn('srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4', className)}>
        <Empty type={type} renderLink={renderCreateLink} noClass />
      </div>
    )
  }

  const hasJournals = data.journals.length > 0

  return (
    <div className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-8', className)}>
      <div className='srd:border-b srd:border-outline-variant/50 srd:pb-8'>
        <header className='srd:flex srd:flex-col srd:gap-4 srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4'>
          <div className='srd:flex srd:items-center srd:gap-4 srd:flex-wrap'>
            <h2 className={cn(typographyVariants({ variant: 'title-md' }))}>Jurnal Baca</h2>

            {type === 'school' && onSchoolClassChange && (
              <SchoolClassSelect
                value={schoolClassValue ?? null}
                onValueChange={onSchoolClassChange}
                showNoClass
                size='sm'
                className='srd:w-full srd:max-w-[300px]'
              />
            )}
          </div>

          <div className='srd:flex srd:items-center srd:justify-center srd:w-full'>
            <WeekChanger
              className='srd:w-full srd:justify-between srd:max-w-[500px]'
              handleClickFarLeft={() => onWeekChange(new Date(earliestMonday))}
              handleClickLeft={() => {
                const prev = new Date(activeMonday)
                prev.setDate(prev.getDate() - 7)
                onWeekChange(prev)
              }}
              handleClickRight={() => {
                const next = new Date(activeMonday)
                next.setDate(next.getDate() + 7)
                onWeekChange(next)
              }}
              handleClickFarRight={() => onWeekChange(new Date(latestMonday))}
              disabledFarLeft={!canGoLeft}
              disabledLeft={!canGoLeft}
              disabledRight={!canGoRight}
              disabledFarRight={!canGoRight}
              text={formatWeekLabel(activeMonday, latestMonday)}
              subText={`${formatDate(activeMonday, { dateStyle: 'short' })} - ${formatDate(getSunday(activeMonday), { dateStyle: 'short' })}`}
            />
          </div>
        </header>
      </div>

      {!hasJournals ? (
        <div className='srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4'>
          <Empty
            type={type}
            renderLink={renderCreateLink}
            noStudents={!data.hasAnyChildren}
            teamId={schoolClassValue}
          />
        </div>
      ) : (
        <div className='srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4 srd:flex srd:flex-col srd:gap-y-8'>
          <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4'>
            <MetricCard
              label='Berbintang 5 hari+'
              value={fiveStarPlusCount}
              unit='anak'
              format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
            />
            <MetricCard
              label='Rata-rata hari berbintang'
              value={avgStarredDays}
              unit='hari / anak'
              format={{ minimumFractionDigits: 0, maximumFractionDigits: 1 }}
            />
          </div>

          {(withSearch === true || withDownloadButton === true) && (
            <div className='srd:flex srd:items-center srd:justify-between srd:gap-2 srd:flex-wrap'>
              {withSearch && (
                <InputGroup size='sm' className='srd:max-w-xs'>
                  <InputGroupAddon align='inline-start'>
                    <Search className='srd:text-blue-100 srd:size-4' />
                  </InputGroupAddon>
                  <InputGroupInput
                    placeholder='Cari nama...'
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </InputGroup>
              )}
              {withDownloadButton && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button variant='ghost' className='srd:ml-auto srd:text-neutral-black'>
                        <Download className='srd:size-4 srd:mr-2' />
                        Unduh
                      </Button>
                    }
                  />
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem onClick={downloadCSV}>CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void downloadPoster()} disabled={posterMutation.isPending}>
                      {posterMutation.isPending ? 'Menyiapkan…' : 'Poster PDF'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          <div className='srd:w-full srd:overflow-hidden srd:rounded-md srd:border'>
            <TableComponent>
              <TableHeader className='srd:bg-grey-30'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className='srd:hover:bg-transparent'>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className='srd:text-nowrap'>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className='srd:bg-neutral-white'>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className='srd:hover:bg-transparent'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </TableComponent>
          </div>

          <p
            className={cn(
              typographyVariants({ variant: 'body-lg' }),
              'srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:text-nowrap',
            )}
          >
            <img src={getStaticAssetUrl('illustration/bintang-senyum.svg')} draggable={false} className='srd:size-6' />

            <span>= ≥{STAR_MIN_MINUTES} menit</span>
          </p>
        </div>
      )}
    </div>
  )
}
